import {
  type CreateAdminTypePropertyDto,
  type UpdateAdminTypePropertyDto,
  type V2AdminTypePropertiesPageDto,
  type V2AdminTypePropertiesSummaryDto,
  type V2AdminTypePropertyDto,
  v2AdminTypePropertiesControllerCreate,
  v2AdminTypePropertiesControllerDetail,
  v2AdminTypePropertiesControllerList,
  v2AdminTypePropertiesControllerLookup,
  v2AdminTypePropertiesControllerRemove,
  v2AdminTypePropertiesControllerSummary,
  v2AdminTypePropertiesControllerUpdate
} from '@idc-forms/api-client'
import {
  V2AdminTypePropertiesPageSchema,
  V2AdminTypePropertiesSummarySchema,
  V2AdminTypePropertyLookupSchema,
  V2AdminTypePropertySchema
} from '@idc-forms/schemas/api-v2/admin-type-properties'
import { assertShape } from '@/shared/api/assert-shape'
import { bearer, v2RequestOptions } from '@/shared/api/v2-request'

/** Entrada mínima del selector de subtipos (`typ_` + código + nombre + proyecto `prj_`). */
export type TypePropertyV2LookupOption = {
  id: string
  code: string
  name: string | null
  project: string
}

/**
 * Administración de subtipos contra la API v2 (ADR 0019/0020). Consume el SDK
 * generado con el Bearer de la sesión staff v2, pagina por cursor y usa el
 * contrato de consulta rico. Identifica todo por id público (`typ_`); el proyecto
 * dueño viaja por `prj_`, la autoría por `usr_`, nunca como ObjectId.
 *
 * `v2RequestOptions` es OBLIGATORIO en cada llamada: trae el `querySerializer`
 * con bracket notation. El serializer por defecto del SDK usa `deepObject` de UN
 * solo nivel y explota en runtime ante cualquier filtro anidado del ADR 0020.
 */

/** Parámetros amigables del listado; el servicio los mapea al query del SDK. */
export type TypePropertiesAdminV2Query = {
  filters?: Record<string, unknown>
  /** `campo:asc|desc` (un solo campo; el cursor desempata por id). */
  sort?: string
  search?: string
  pageSize?: number
  after?: string
  before?: string
}

export type TypePropertiesAdminV2Row = V2AdminTypePropertiesPageDto['data'][number]

function toListQuery(query: TypePropertiesAdminV2Query) {
  return {
    ...(query.filters && Object.keys(query.filters).length ? { filters: query.filters } : {}),
    ...(query.sort ? { sort: query.sort } : {}),
    ...(query.search ? { search: query.search } : {}),
    ...(query.pageSize ? { 'page[size]': query.pageSize } : {}),
    ...(query.after ? { 'page[after]': query.after } : {}),
    ...(query.before ? { 'page[before]': query.before } : {})
  }
}

export const typePropertiesAdminV2Service = {
  /** Subtipos activos (`typ_` + código + nombre + proyecto `prj_`) para los selects en cascada. */
  lookup: async (accessToken: string): Promise<TypePropertyV2LookupOption[]> => {
    const result = await v2AdminTypePropertiesControllerLookup({
      ...v2RequestOptions,
      headers: bearer(accessToken)
    })
    return assertShape(V2AdminTypePropertyLookupSchema, result.data).data.map((subtype) => ({
      id: subtype.id,
      code: subtype.code,
      name: subtype.name ?? null,
      project: subtype.project
    }))
  },

  list: async (
    accessToken: string,
    query: TypePropertiesAdminV2Query
  ): Promise<V2AdminTypePropertiesPageDto> => {
    const result = await v2AdminTypePropertiesControllerList({
      ...v2RequestOptions,
      headers: bearer(accessToken),
      query: toListQuery(query)
    })
    return assertShape(V2AdminTypePropertiesPageSchema, result.data)
  },

  summary: async (
    accessToken: string,
    query: Pick<TypePropertiesAdminV2Query, 'filters' | 'search'>
  ): Promise<V2AdminTypePropertiesSummaryDto> => {
    const result = await v2AdminTypePropertiesControllerSummary({
      ...v2RequestOptions,
      headers: bearer(accessToken),
      query: toListQuery(query)
    })
    return assertShape(V2AdminTypePropertiesSummarySchema, result.data)
  },

  detail: async (accessToken: string, id: string): Promise<V2AdminTypePropertyDto> => {
    const result = await v2AdminTypePropertiesControllerDetail({
      ...v2RequestOptions,
      headers: bearer(accessToken),
      path: { id }
    })
    return assertShape(V2AdminTypePropertySchema, result.data)
  },

  create: async (
    accessToken: string,
    body: CreateAdminTypePropertyDto
  ): Promise<V2AdminTypePropertyDto> => {
    const result = await v2AdminTypePropertiesControllerCreate({
      ...v2RequestOptions,
      headers: bearer(accessToken),
      body
    })
    return result.data
  },

  update: async (
    accessToken: string,
    id: string,
    body: UpdateAdminTypePropertyDto
  ): Promise<V2AdminTypePropertyDto> => {
    const result = await v2AdminTypePropertiesControllerUpdate({
      ...v2RequestOptions,
      headers: bearer(accessToken),
      path: { id },
      body
    })
    return result.data
  },

  remove: async (accessToken: string, id: string): Promise<void> => {
    await v2AdminTypePropertiesControllerRemove({
      ...v2RequestOptions,
      headers: bearer(accessToken),
      path: { id }
    })
  }
}
